import { InnerBox } from "@/components/elements/InnerBox";
import { Stack, Typography, Box } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/elements/Button";
import { black } from "@/theme/colors";
import Divider from "@mui/material/Divider";
import { FCC } from "@/types/common";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { TCompanyForm, TJobCompany } from "@/features/company/types/CompanyForm";
import { EMPTY_STR } from "@/constants/string";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { useEnum } from "@/api/services/global/enum";
import { findLabelByValue } from "@/utils/object";
import SearchIcon from "@mui/icons-material/Search";
import { JobStatusTag } from "@/features/job/components/JobStatusTag";
import { PastJobsModal } from "@/features/company/components/company-detail/PastJobsModal";
import { useVisible } from "@/hooks/useVisible";
import { CLOSED_JOB_STATUS, MAX_DISPLAY_JOBS } from "@/features/company/constants/common";

type TCompanyJobInfoProps = {
  defaultValues: Partial<TCompanyForm>;
};

export const CompanyJobInfo: FCC<TCompanyJobInfoProps> = ({ defaultValues }) => {
  const { companyId } = useParams();
  const { data: salaryTypeOptions } = useEnum("ESalaryType");
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const pastJobsModal = useVisible();

  const { jobs } = defaultValues;

  // Show latest 5 jobs regardless of status (sorted by updatedAt, newest first).
  const latestJobs = (jobs ?? [])
    .slice()
    .sort((a, b) => {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, MAX_DISPLAY_JOBS);

  const handleToJobDetail = (jobId: number) => getDynamicRoute(AppRoutes.jobDetail, { jobId: String(jobId) });

  const getWorkplace = (job: TJobCompany) => job.workplace || EMPTY_STR.TEXT;

  const getEndDate = (job: TJobCompany) => {
    const { endDate, closedAt, status } = job;
    const useClosedAt = status === CLOSED_JOB_STATUS && closedAt && (!endDate || dayjs(closedAt).isBefore(dayjs(endDate)));
    const deadline = useClosedAt ? closedAt : endDate;
    return deadline ? dayjs(deadline).format(DATE_FORMAT) : EMPTY_STR.DATE;
  };

  // Display salary from 総支給額 (jobSalarySummaries[0].totalAmount) formatted as 万円 (e.g. 月給25万円).
  // Prefix with salary type label; default to 月給 when salaryType missing or unmapped.
  const getSalary = (job: TJobCompany) => {
    const totalAmount = job.jobSalarySummaries?.[0]?.totalAmount;
    if (totalAmount === undefined || totalAmount === null) return EMPTY_STR.TEXT;
    const label = findLabelByValue(salaryTypeOptions, job.salaryType) || "月給";
    return `${label}${totalAmount / 10000}万円`;
  };

  return (
    <>
      <InnerBox showDivider={false} padding={3}>
        <Stack spacing={3}>
          <Typography variant="sub16Bold" color={black[30]}>
            求人情報
          </Typography>

          {latestJobs.length === 0 && (
            <Typography variant="body14Light" color={black[70]}>
              現在、求人情報はありません。
            </Typography>
          )}

          {latestJobs.map((job) => (
            <InnerBox key={job.id} showDivider={false} padding={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="sub16Bold"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
                  >
                    {job.jobName}
                  </Typography>
                  <JobStatusTag status={job.status} title={findLabelByValue(jobStatusOptions, job.status)} variant="bold" />
                </Stack>
                <Button
                  variant="ghost"
                  size="sm"
                  component={Link}
                  to={handleToJobDetail(job.id)}
                  target="_blank"
                  sx={{ flexShrink: 0 }}
                  onClick={(e) => (e.currentTarget as HTMLElement).blur()}
                >
                  詳細を見る
                </Button>
              </Stack>
              <Stack direction="row" gap={1} mt={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="body14Light" color={black[70]} sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                  {getSalary(job)}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography
                  variant="body14Light"
                  color={black[70]}
                  title={getWorkplace(job)}
                  sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {getWorkplace(job)}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body14Light" color={black[70]} sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                  応募数：{job.caseCount != null ? `${job.caseCount}人` : EMPTY_STR.TEXT}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body14Light" color={black[70]} sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                  募集期限：{getEndDate(job)}
                </Typography>
              </Stack>
            </InnerBox>
          ))}

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outline"
              size="sm"
              startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
              onClick={pastJobsModal.open}
              disabled={(jobs ?? []).length === 0}
            >
              過去の求人一覧
            </Button>
          </Box>
        </Stack>
      </InnerBox>

      <PastJobsModal open={pastJobsModal.visible} onClose={pastJobsModal.close} companyId={companyId ? Number(companyId) : undefined} />
    </>
  );
};
